/* =========================================================================
 * NEVER DUMP AN OBJECT AT A REP.
 *
 * 🔴 WHY. Brian opened a record on his phone and `{"d":270.16,"allDelivered":
 * 223.71}` was on screen, in the middle of the explanation of why there was no
 * figure to quote. A rep cannot read that, cannot act on it, and cannot repeat
 * it to a customer — and the one thing it reliably communicates is that the
 * software is broken.
 *
 * ⚠️ IT DID NOT COME FROM THIS CODEBASE. `allDelivered` appears nowhere in
 * src/, and nothing on that path calls `JSON.stringify`. It arrived INSIDE a
 * string the backend served — a reason or basis field carrying a serialized
 * object where a sentence was expected — and this app rendered it faithfully,
 * because rendering the server's own words verbatim is the right default and
 * is deliberate everywhere else.
 *
 * So the fix is not to stop trusting server prose. It is to notice when what
 * arrived is not prose. A machine-shaped payload is recognisable, and the
 * honest thing to show in its place is a short statement that the explanation
 * did not arrive — never the payload, and never a blank, because a blank where
 * a reason belongs reads as "there is no reason".
 *
 * ⚠️ AND THE FALLBACK NAMES THE FIELD, NOT THE REP'S PROBLEM. "The source did
 * not send a readable explanation" is true, points at the right system, and
 * does not ask a rep to do anything about it.
 * ========================================================================= */

const S = (v) => (typeof v === 'string' ? v : '')

/** A whole string that is just a serialized object or array. */
const WHOLLY_JSON = /^\s*[[{][\s\S]*[\]}]\s*$/

/** A serialized object EMBEDDED in otherwise-real prose. */
const EMBEDDED_JSON = /[[{](?:\s*"[^"]*"\s*:\s*[^,}]+\s*,?)+\s*[}\]]/g

/** The shapes a careless `${}` leaves behind. */
const STRINGIFIED = /\[object [A-Za-z]+\]|\bundefined\b|\bNaN\b/

/**
 * `{ text, suppressed, why }`.
 *
 * `text` is safe to render. `suppressed` is true when something machine-shaped
 * was removed, so a caller that wants to say so can — and so a probe can
 * assert that it happened rather than trusting that it did.
 */
export function plainText(value, { fallback = 'The source did not send a readable explanation.' } = {}) {
  /* ⚠️ A NON-STRING IS THE WORST CASE AND THE EASIEST TO GET WRONG. React
   * renders an object by throwing; a template literal renders it as
   * "[object Object]". Neither is acceptable, so a non-string never becomes
   * text at all. A number IS legitimate prose in some fields, so it is kept. */
  if (value == null) return { text: '', suppressed: false, why: null }
  if (typeof value === 'number' && Number.isFinite(value)) {
    return { text: String(value), suppressed: false, why: null }
  }
  if (typeof value !== 'string') {
    return { text: fallback, suppressed: true, why: 'the field held an object, not a sentence' }
  }

  const raw = value.trim()
  if (!raw) return { text: '', suppressed: false, why: null }

  if (WHOLLY_JSON.test(raw)) {
    return { text: fallback, suppressed: true, why: 'the field held serialized data, not a sentence' }
  }

  let out = raw.replace(EMBEDDED_JSON, '').replace(/\s{2,}/g, ' ').trim()
  const strippedSomething = out !== raw
  /* Tidy the punctuation the removal can strand — "no figure to quote ." */
  out = out.replace(/\s+([.,;:])/g, '$1').replace(/^[\s,;:—-]+/, '').trim()

  if (STRINGIFIED.test(out)) {
    return { text: fallback, suppressed: true, why: 'the field held an unrendered value' }
  }
  /* Whatever is left has to still be a sentence. If stripping the payload left
   * a fragment, the fragment is worse than the honest fallback — a rep reading
   * half a reason will act on it. */
  if (strippedSomething && out.replace(/[^a-z]/gi, '').length < 12) {
    return { text: fallback, suppressed: true, why: 'what was left was not a sentence' }
  }

  return { text: out, suppressed: strippedSomething, why: strippedSomething ? 'serialized data removed' : null }
}

/** The common case: just the text. */
export const safeText = (v, opts) => plainText(v, opts).text
